import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { Alert } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  clearMyAuctions,
  getAuction,
  handleAuctionEvent,
  searchMyAuctions,
  setVehicleFilter,
} from './MyVehiclesActions';
import { AUCTION_DELETED, SSE_SELLER, SSEClient } from '../api/SSEClient';
import RouterPaths from '../constants/RouterPaths';
import { MY_VEHICLES_REFRESH_SUCCESS } from './MyVehiclesReducer';
import { AuctionStatus } from '../constants/AuctionStatus';
import AuctionListTemplate, {
  AuctionListTemplateContextType,
} from '../common/components/new/AuctionListTemplate';
import { AuctionSortType } from '../constants/AuctionSortType';
import AuctionListItem from '../common/components/new/AuctionListItem';
import { auctionListType } from '../constants/AuctionListType';

const sseClient = new SSEClient(SSE_SELLER);

function MyVehiclesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const itemsRef = useRef({});
  const [scrollTo, setScrollTo] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [auctionsIds, setAuctionsIds] = useState([]);
  const latestCriteriaRef = useRef({});

  const status = useSelector((state) => state.myVehicles.get('filter'));
  const hasMore = useSelector((state) => state.myVehicles.get('hasMore'));
  const searchItems = useSelector((state) => state.myVehicles.get('items').toJS());
  const searchTotalItems = useSelector((state) => state.myVehicles.get('totalItems'));
  const isBuyer = useSelector((state) => state.auth.get('isBuyer'));
  const isSeller = useSelector((state) => state.auth.get('isSeller'));
  const [searchParams, setSearchParams] = useState({
    offset: 0,
    status: (location && location.state && location.state.filter) || status,
    search: '',
    sort: AuctionSortType.ENDING_SOONEST,
  });

  const updateSearchItems = (newItem) => {
    dispatch({
      type: MY_VEHICLES_REFRESH_SUCCESS,
      payload: {
        item: newItem,
      },
    });
  };

  useEffect(() => {
    if (!location || !location.state || !location.state.offset) {
      clearResults();
    }
  }, []);

  useEffect(() => {
    if (!isSeller) return undefined;
    sseClient.listen(auctionsIds, (response) => {
      dispatch(
        handleAuctionEvent(response, () => {
          if (response.type === AUCTION_DELETED) {
            dispatch(
              searchMyAuctions(
                {
                  ...searchParams,
                  ...latestCriteriaRef.current,
                },
                (auctionsIds) => {
                  setAuctionsIds((prev) => prev.concat(auctionsIds));
                  setLoaded(true);
                },
              ),
            );
          } else if (response?.auctionId) {
            dispatch(
              getAuction(response.auctionId, (auction) => {
                updateSearchItems(auction);
              }),
            );
          }
        }),
      );
    });
    return () => {
      sseClient.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionsIds, dispatch, searchParams, isSeller]);

  const scrollToItem = () => {
    if (!scrollTo) return;
    if (itemsRef.current[scrollTo]) {
      itemsRef.current[scrollTo].scrollIntoView({ behavior: 'auto', block: 'center' });
      setScrollTo(null);
    }
  };

  useEffect(() => {
    if (!isSeller) return;
    if (location && location.state && location.state.offset && location.state.auctionId) {
      setSearchParams((prev) => ({
        ...prev,
        offset: _.get(location, 'state.offset', 0) + 1,
        status: _.get(location, 'state.filter', status),
      }));
      setScrollTo(location.state.auctionId);
      navigate(location.pathname + location.search, { replace: true, state: {} });
      return;
    }

    if (searchParams.offset === 0) return;

    dispatch(
      searchMyAuctions(
        {
          ...searchParams,
          ...latestCriteriaRef.current,
        },
        (auctionsIds) => {
          setAuctionsIds(auctionsIds);
          setLoaded(true);
          scrollToItem();
        },
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, dispatch, isSeller]);

  const loadData = () => {
    setSearchParams((prev) => ({
      ...prev,
      offset: prev.offset + 1,
    }));
  };

  // seed auctionsIds for SSE after the initial child-driven load
  useEffect(() => {
    if (!isSeller) return;
    if (auctionsIds.length === 0 && Array.isArray(searchItems) && searchItems.length > 0) {
      setAuctionsIds(searchItems.map((i) => i.id));
      setLoaded(true);
    }
  }, [searchItems, auctionsIds.length, isSeller]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clearResults = () => {
    dispatch(clearMyAuctions());
    setAuctionsIds([]);
    setLoaded(false);
  };

  useEffect(() => {
    if (location) {
      const { hash } = location;
      if (hash && hash === '#vehicleFilter') {
        navigate(RouterPaths.MY_VEHICLES);
      } else {
        dispatch(setVehicleFilter((location.state && location.state.filter) || status));
      }
    }

    return () => {
      dispatch(setVehicleFilter(AuctionStatus.PUBLISHED)); // reset to first filter
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuctionListTemplate
      context={AuctionListTemplateContextType.SELLING}
      searchItems={searchItems}
      onLoadMore={loadData}
      onCriteriaChange={(crit) => {
        latestCriteriaRef.current = crit || {};
      }}
      onSortChange={(newSort) => {
        const base = {
          ...searchParams,
          offset: 0,
          sort: newSort,
        };
        dispatch(
          searchMyAuctions({ ...base, ...latestCriteriaRef.current }, (auctionsIds) => {
            setAuctionsIds(auctionsIds);
            setLoaded(true);
          }),
        );
        setSearchParams((prev) => ({ ...prev, offset: 0, sort: newSort }));
      }}
    >
      {searchTotalItems > 0 ? (
        <>
          {searchItems.map((item) => (
            <AuctionListItem
              item={item}
              key={item.id}
              context={AuctionListTemplateContextType.SELLING}
              listType={auctionListType.MY_VEHICLES}
            />
          ))}
        </>
      ) : (
        <div className="mt-2">
          {searchParams.search ? (
            <Alert color="info">Oops, we could not find any result for the given phrase</Alert>
          ) : (
            <Alert color="info">Looks like there are no offers yet.</Alert>
          )}
        </div>
      )}
    </AuctionListTemplate>
  );
}

export default MyVehiclesPage;
